import { useEffect, useState, useRef } from "react";

const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder,
  name,
  required,
  onToggle,
  isKPITable = false,
  autoOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    options.find(opt => opt.value == value) || null
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setSelectedOption(options.find(opt => opt.value == value) || null);
  }, [value, options]);

  useEffect(() => {
    if (isKPITable) {
      const filtered = options.filter(option => {
        const searchableText = option.searchText || 
                              (typeof option.label === 'string' ? option.label : '') ||
                              option.companyName || '';
        return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, isKPITable]);

  useEffect(() => {
    if (autoOpen && !isOpen && isKPITable) {
      setIsOpen(true);
      if (inputRef.current) {
        setTimeout(() => inputRef.current.focus(), 100);
      }
      if (onToggle) onToggle(true);
    }
  }, [autoOpen, isOpen, isKPITable, onToggle]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        if (onToggle) onToggle(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState && isKPITable && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
    if (onToggle) onToggle(newState);
  };

  const handleSelect = (option) => {
    setSelectedOption(option);
    onChange({ target: { name, value: option.value } });
    setIsOpen(false);
    setSearchTerm('');
    if (onToggle) onToggle(false);
  };

  const renderOptionContent = (option) => {
    if (option.companyName && option.contactName) {
      return (
        <div className="py-1">
          <div className="font-semibold text-gray-900">{option.companyName}</div>
          <div className="text-sm text-gray-500">{option.contactName}</div>
        </div>
      );
    }
    return option.label;
  };

  const getDisplayText = (option) => {
    if (option.companyName && option.contactName) {
      return `${option.companyName} - ${option.contactName}`;
    }
    return typeof option.label === 'string' ? option.label : option.searchText || '';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
      >
        <span className={selectedOption ? "text-gray-900" : "text-gray-400"}>
          {selectedOption ? getDisplayText(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg ${isKPITable ? 'min-h-[450px]' : ''} max-h-60 overflow-auto`}>
          {isKPITable && (
            <>
              <div className="p-2 border-b">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search companies..."
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className={` ${isKPITable ? 'max-h-[400px]' : 'max-h-40'} overflow-auto`}>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option)}
                      className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none text-sm"
                    >
                      {renderOptionContent(option)}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500 text-sm">No companies found</div>
                )}
              </div>
            </>
          )}

          {!isKPITable && (
            options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option)}
                className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              >
                {renderOptionContent(option)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;